import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiSantoLog } from "../../../Services/api";
import Code39 from "../../../Components/Code39";
import {
  PageWrap, List, Row, DeskList, DeskCard, Chip, ViewToggle,
  ModalOverlay, ModalCard, ModalHead, ModalClose, ModalBody,
  SummaryGrid, OrderTable, Empty, Loading,
} from "./style";

const REFRESH_MS = 60000;
const MAX_WAVES_TV = 20;
const VIEW_STORAGE_KEY = "ondas:view";

// Deadline vem como literal 'YYYY-MM-DDTHH:MM:SSZ' (já é horário local) —
// formatamos por regex, sem converter fuso.
function fmtDeadline(ts) {
  if (!ts) return "—";
  const m = String(ts).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return String(ts);
  const [, , mo, d, h, mi] = m;
  return `${d}/${mo} ${h}:${mi}`;
}

// Nível de coerência da onda (verde ≥80 / amarelo 50–79 / vermelho <50)
function coherenceTone(v) {
  if (v == null || v === "") return "neutral";
  const n = Number(v);
  if (n >= 80) return "ok";
  if (n >= 50) return "warn";
  return "danger";
}

const listOf = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

/**
 * Percentual de conclusão da onda.
 *
 * Regra: (pedidos que já saíram de "em separação") ÷ (total de pedidos da onda).
 * Ex.: onda com 10 pedidos, 2 ainda em separação → 80% concluída.
 *
 * ATENÇÃO — depende do backend: hoje o /waves/live devolve SÓ os pedidos em
 * separação, então `total_orders` é o que ainda falta, não o tamanho original
 * da onda. Sem o total de todos os status não dá pra calcular, e o percentual
 * fica oculto no cartão. Basta o endpoint passar a mandar um dos dois:
 *   · completion_percentage   → usado direto, sem conta
 *   · total_orders_all_status → a conta é feita aqui, com o total_orders atual
 */
function completionPercentage(wave) {
  const readyMade = wave?.completion_percentage;
  if (readyMade != null && readyMade !== "") {
    const value = Number(readyMade);
    return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
  }

  const totalAllStatus = Number(wave?.total_orders_all_status);
  const stillInSeparation = Number(wave?.total_orders);
  if (!Number.isFinite(totalAllStatus) || totalAllStatus <= 0) return null;
  if (!Number.isFinite(stillInSeparation)) return null;

  const done = Math.max(0, totalAllStatus - stillInSeparation);
  return Math.min(100, (done / totalAllStatus) * 100);
}

/**
 * Painel público de ondas AO VIVO — GET /api/santolog/waves/live
 * (WMS Sonar + IDWorks, montado na hora; sem banco e sem reordenação manual).
 *
 * As ondas já vêm ordenadas por `priority_order`, então só renderizamos na ordem.
 *
 * URL controla andar, data e modo de exibição (todos opcionais):
 *   /dashboard/ondas                  → visão padrão (computador)
 *   /dashboard/ondas?view=tv          → visão deitada (TV em pé)
 *   /dashboard/ondas?andar=terreo     → térreo + ambos
 *   /dashboard/ondas?andar=primeiro   → primeiro + ambos
 *   /dashboard/ondas?date=2026-08-15  → outra data de inclusão no Sonar
 *
 * Sem ?view, usa a última escolha salva NESTE dispositivo (localStorage);
 * na ausência de escolha, abre na visão padrão.
 */
export default function OndasDashboardLive(props) {
  const search = props?.location?.search || "";
  const params = new URLSearchParams(search);
  const andar = params.get("andar") || "";
  const date = params.get("date") || "";
  const viewParam = (params.get("view") || "").toLowerCase();

  const [view, setView] = useState(() => {
    if (viewParam === "tv" || viewParam === "desk") return viewParam;
    try {
      const saved = window.localStorage.getItem(VIEW_STORAGE_KEY);
      if (saved === "tv" || saved === "desk") return saved;
    } catch (_) { /* localStorage indisponível */ }
    return "desk";
  });
  const isTv = view === "tv";

  const [waves, setWaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // onda aberta no modal
  const [dimToggle, setDimToggle] = useState(false);

  // O modal usa o objeto em memória — pausa o refresh para os dados não
  // trocarem embaixo do operador enquanto ele lê os pedidos.
  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const load = useCallback(async () => {
    if (selectedRef.current) return; // modal aberto: não atualiza
    try {
      const query = {};
      if (andar) query.andar = andar;
      if (date) query.date = date;
      const res = await apiSantoLog.get("waves/live", { params: query });
      setWaves(Array.isArray(res.data?.waves) ? res.data.waves : []);
    } catch (e) {
      console.error("GET waves/live failed:", e);
    } finally {
      setLoading(false);
    }
  }, [andar, date]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // Guarda a preferência por dispositivo e some com o botão na TV
  function toggleView() {
    const next = isTv ? "desk" : "tv";
    setView(next);
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, next); } catch (_) { /* noop */ }
  }
  useEffect(() => {
    if (!isTv) { setDimToggle(false); return undefined; }
    const t = setTimeout(() => setDimToggle(true), 4000);
    return () => clearTimeout(t);
  }, [isTv]);

  // Esc fecha o modal
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  const visible = isTv ? waves.slice(0, MAX_WAVES_TV) : waves;

  // ── MODAL: dados da onda + pedidos vinculados ─────────────────────────────
  function renderModal() {
    const w = selected;
    if (!w) return null;
    const orders = listOf(w.orders);
    const transportadoras = listOf(w.transportadoras);
    const separadores = listOf(w.separadores);
    const overdue = w.overdue_count ?? 0;
    const coh = w.wave_coherence_percentage;

    return (
      <ModalOverlay onClick={() => setSelected(null)}>
        <ModalCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <ModalHead>
            <div className="title">
              <span className="rank">{w.priority_order ?? "—"}</span>
              <h3>Onda {w.wave_number ?? "—"}</h3>
              {w.status && <Chip $tone="accent">{w.status}</Chip>}
              {coh != null && (
                <Chip $tone={coherenceTone(coh)}>coerência {Number(coh).toFixed(0)}%</Chip>
              )}
              {overdue > 0 && (
                <Chip $tone="danger">⚠ {overdue} atrasado{overdue > 1 ? "s" : ""}</Chip>
              )}
              {w.andar && <Chip>{w.andar}</Chip>}
            </div>
            <ModalClose onClick={() => setSelected(null)} title="Fechar (Esc)">×</ModalClose>
          </ModalHead>

          <SummaryGrid>
            <div className="cell">
              <span className="k">Pedidos</span>
              <span className="v">{w.total_orders ?? orders.length}</span>
            </div>
            <div className="cell">
              <span className="k">Itens</span>
              <span className="v">{w.total_items ?? "—"}</span>
            </div>
            <div className="cell">
              <span className="k">Embarque + próximo</span>
              <span className="v">{fmtDeadline(w.nearest_deadline)}</span>
            </div>
            <div className="cell">
              <span className="k">Embarque + distante</span>
              <span className="v">{fmtDeadline(w.latest_deadline)}</span>
            </div>
            {w.orders_at_nearest != null && (
              <div className="cell">
                <span className="k">No + próximo</span>
                <span className="v">{w.orders_at_nearest}</span>
              </div>
            )}
            {transportadoras.length > 0 && (
              <div className="cell">
                <span className="k">Transportadoras</span>
                <span className="v sm">{transportadoras.join(" · ")}</span>
              </div>
            )}
            {separadores.length > 0 && (
              <div className="cell">
                <span className="k">Separadores</span>
                <span className="v sm">{separadores.join(" · ")}</span>
              </div>
            )}
          </SummaryGrid>

          <ModalBody>
            {orders.length === 0 ? (
              <Empty>Sem pedidos vinculados a esta onda.</Empty>
            ) : (
              <OrderTable>
                <thead>
                  <tr>
                    <th>Referência</th>
                    <th>Pré-faturamento</th>
                    <th>NF</th>
                    <th className="c">Volumes</th>
                    <th>Data Embarque</th>
                    <th>Separadores</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o, i) => (
                    <tr key={o.referencia ?? i}>
                      <td className="mono">{o.referencia ?? "—"}</td>
                      <td className="mono dim">{o.prefaturamento ?? "—"}</td>
                      <td className="mono dim">{o.nfe_number ?? "—"}</td>
                      <td className="mono c">{o.volumes ?? "—"}</td>
                      <td className="mono">
                        {o.deadline_missing ? "—" : fmtDeadline(o.data_expedicao)}
                      </td>
                      <td className="dim">{listOf(o.separadores).join(" · ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </OrderTable>
            )}
          </ModalBody>
        </ModalCard>
      </ModalOverlay>
    );
  }

  return (
    <PageWrap $scroll={!isTv}>
      {loading && !visible.length && <Loading>Carregando ondas…</Loading>}
      {!loading && !visible.length && (
        <Empty>Nenhuma onda em separação no momento.</Empty>
      )}

      {/* ── VISÃO TV: cartões rotacionados, 2 por coluna ── */}
      {isTv && visible.length > 0 && (
        <List>
          {visible.map((w, i) => {
            const num = w.wave_number ?? w.number ?? w.id;
            const rank = w.priority_order ?? i + 1;
            const col = Math.floor(i / 2) + 1;   // 2 ondas por coluna
            const rowPos = i % 2 === 0 ? 2 : 1;   // rank ímpar embaixo, par em cima

            // Coluna esquerda empilhada ao longo do cartão:
            //   separadores (um por linha) → número + embarque → % concluída → rank
            const separadores = listOf(w.separadores);
            const percentage = completionPercentage(w);
            const deadline = fmtDeadline(w.nearest_deadline);
            const hasDeadline = deadline !== "—";

            return (
              <Row
                key={num ?? i}
                style={{ gridColumn: col, gridRow: rowPos }}
                onClick={() => setSelected(w)}
                title="Ver pedidos da onda"
              >
                <div className="left">
                  {separadores.length > 0 && (
                    <div className="seps">
                      {separadores.map((nome, idx) => (
                        <div key={`${nome}-${idx}`}>{nome}</div>
                      ))}
                    </div>
                  )}

                  <div className="mid">
                    <div className="onda">{num ?? "—"}</div>
                    {hasDeadline && <div className="dl">{deadline}</div>}
                  </div>

                  {percentage != null && (
                    <div className={`pct ${coherenceTone(percentage)}`}>
                      {Math.round(percentage)}%
                    </div>
                  )}

                  <span className="rank">{rank}</span>
                </div>
                <div className="barcode">
                  <Code39 value={num} vertical fill height={46} />
                </div>
              </Row>
            );
          })}
        </List>
      )}

      {/* ── VISÃO PADRÃO (computador): cartões horizontais com os dados ── */}
      {!isTv && visible.length > 0 && (
        <DeskList>
          {visible.map((w, i) => {
            const num = w.wave_number ?? w.number ?? w.id;
            const rank = w.priority_order ?? i + 1;
            const overdue = w.overdue_count ?? 0;
            const coh = w.wave_coherence_percentage;
            const transportadoras = listOf(w.transportadoras);
            return (
              <DeskCard
                key={num ?? i}
                $overdue={overdue > 0}
                onClick={() => setSelected(w)}
                title="Ver pedidos da onda"
              >
                <div className="head">
                  <span className="rank">{rank}</span>
                  <span className="num">{num ?? "—"}</span>
                </div>
                <div className="barcode">
                  <Code39 value={num} height={58} />
                </div>
                <div className="meta">
                  <span>Pedidos <b>{w.total_orders ?? 0}</b></span>
                  <span>Itens <b>{w.total_items ?? 0}</b></span>
                  <span>Embarque <b>{fmtDeadline(w.nearest_deadline)}</b></span>
                </div>
                <div className="chips">
                  {overdue > 0 && (
                    <Chip $tone="danger">⚠ {overdue} atrasado{overdue > 1 ? "s" : ""}</Chip>
                  )}
                  {coh != null && (
                    <Chip $tone={coherenceTone(coh)}>coerência {Number(coh).toFixed(0)}%</Chip>
                  )}
                  {w.andar && <Chip>{w.andar}</Chip>}
                  {transportadoras.length > 0 && (
                    <Chip title={transportadoras.join(" · ")}>
                      {transportadoras[0]}
                      {transportadoras.length > 1 ? ` +${transportadoras.length - 1}` : ""}
                    </Chip>
                  )}
                </div>
              </DeskCard>
            );
          })}
        </DeskList>
      )}

      <ViewToggle
        type="button"
        $dim={dimToggle}
        onClick={toggleView}
        onMouseEnter={() => setDimToggle(false)}
        title={isTv ? "Mudar para a visão de computador" : "Mudar para a visão de TV (deitada)"}
      >
        {isTv ? "🖥 Visão computador" : "📺 Visão TV"}
      </ViewToggle>

      {renderModal()}
    </PageWrap>
  );
}
